import React from 'react';
import { Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { resolveInternalHref } from '../../../utils/internalHref';
import Image from '../../Image/Image';

const DEFAULT_ICON_SRC =
  'https://assets.askhippo.co.za/Icons/common/Union_Black.svg';

// Pass `to` instead of `href` for an in-app destination: the link then
// navigates client-side instead of reloading the whole application. `href`
// still renders a plain anchor, which is what external URLs and any
// destination whose screen does not yet handle a pathname change need.
const LinkWithIconComponent = ({
  href,
  to,
  children,
  component,
  showIcon = true,
  sx = {},
  iconSrc = DEFAULT_ICON_SRC,
  iconAlt = 'Link icon',
  iconSize = '1rem',
  target,
  rel,
  onClick,
  ...rest
}) => {
  const normalizedHref =
    typeof href === 'string' ? href.trim() : href || undefined;
  const normalizedTo =
    typeof to === 'string'
      ? to.trim()
      : (target !== '_blank' && resolveInternalHref(normalizedHref)) ||
        undefined;
  const isRouterLink = Boolean(normalizedTo) && !component;
  const isLink = Boolean(normalizedHref) || Boolean(normalizedTo);
  const shouldShowIcon = showIcon && isLink;
  const resolvedComponent = isRouterLink
    ? RouterLink
    : component || (isLink ? 'a' : 'span');
  const destinationProps = isRouterLink
    ? { to: normalizedTo }
    : { href: normalizedHref || normalizedTo || undefined };

  return (
    <Box
      component={resolvedComponent}
      {...destinationProps}
      target={target}
      rel={rel}
      onClick={onClick}
      {...rest}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'pre-line',
        gap: '6px',
        maxWidth: '100%',
        color: 'inherit',
        textDecoration: 'none',
        verticalAlign: 'middle',
        cursor: isLink ? 'pointer' : 'default',
        '&:focus-visible': {
          outline: '2px solid currentColor',
          outlineOffset: '4px',
          borderRadius: '8px',
        },
        ...sx,
      }}
    >
      <Box component="span" sx={{ minWidth: 0 }}>
        {children}
      </Box>
      {shouldShowIcon && (
        <Image
          width={16}
          height={16}
          src={iconSrc}
          alt={iconAlt}
          sx={{
            width: iconSize,
            height: iconSize,
            flexShrink: 0,
          }}
        />
      )}
    </Box>
  );
};

export default LinkWithIconComponent;
